import {
    SchemaEntity,
    SchemaAttribute,
    ProcessedSchema,
    ProcessedSchemaAttribute,
    SchemaEntityResponse,
    SchemaAttributeResponse
} from '../types/SchemaTypes';

/**
 * Combine schema entities and attributes into unified schema structure
 */
export function combineSchemaData(
    schemaEntitiesResponse: SchemaEntityResponse,
    schemaAttributesResponse: SchemaAttributeResponse
): ProcessedSchema[] {
    const entities: SchemaEntity[] = schemaEntitiesResponse.Resources || [];
    const attributes: SchemaAttribute[] = schemaAttributesResponse.Resources || [];

    // Step 1: Build attribute name → attribute info map (excluding circular Entities property)
    // Use DisplayName as the key for lookup, but store both DisplayName and Id
    const attributeInfo: Record<string, ProcessedSchemaAttribute> = attributes.reduce(
        (acc: Record<string, ProcessedSchemaAttribute>, attr) => {
            // Use DisplayName as the property name (for matching entity properties)
            // Use Id (with $ prefix) as the identifier (for attribute references)
            const attrId = attr.Id || attr.DisplayName || '';
            const attrName = attr.DisplayName || attrId.replace(/^\$\.?/, '');

            acc[attrName] = {
                name: attrName, // Use DisplayName (without $ prefix) as the property name
                id: attrId,     // Store Id (with $ prefix) separately for attribute references
                type: attr.Type || 'string',
                description: attr.Description || `Attribute: ${attrName}`,
                required: attr.Required || false,
                multiValued: attr.Array || false,
                caseExact: attr.CaseExact || false,
                computed: attr.Computed || false,
                indexed: attr.Indexed || false,
                isReference: attr.IsReference || false,
                isReferenceTo: attr.IsReferenceTo || [],
                unique: attr.Unique || false,
                mutability: 'readonly',
                returned: 'default',
                subAttributes: []
            };
            return acc;
        },
        {} as Record<string, ProcessedSchemaAttribute>
    );

    // Step 2: Iterate through schema entities and build entity → attributes map
    const entityAttributesMap: Record<string, ProcessedSchemaAttribute[]> = {};

    entities.forEach((entity) => {
        const entityName = entity.DisplayName;

        // Initialize empty attributes array for this entity
        entityAttributesMap[entityName] = [];

        // Only add attributes that are already defined for this entity
        if (entity.Attributes && Array.isArray(entity.Attributes)) {
            entity.Attributes.forEach((entityAttr) => {
                // Find the full attribute definition from the attributes list
                const fullAttribute = attributes.find(attr => attr.DisplayName === entityAttr.DisplayName);

                if (fullAttribute) {
                    // Use DisplayName as the property name (for matching entity properties)
                    // Use Id (with $ prefix) as the identifier (for attribute references)
                    const attrId = fullAttribute.Id || fullAttribute.DisplayName || '';
                    const attrName = fullAttribute.DisplayName || attrId.replace(/^\$\.?/, '');

                    const attributeInfo = {
                        name: attrName, // Use DisplayName (without $ prefix) as the property name
                        id: attrId,     // Store Id (with $ prefix) separately for attribute references
                        type: fullAttribute.Type || 'string',
                        description: fullAttribute.Description || `Attribute: ${attrName}`,
                        required: fullAttribute.Required || false,
                        multiValued: fullAttribute.Array || false,
                        caseExact: fullAttribute.CaseExact || false,
                        computed: fullAttribute.Computed || false,
                        indexed: fullAttribute.Indexed || false,
                        isReference: fullAttribute.IsReference || false,
                        isReferenceTo: fullAttribute.IsReferenceTo || [],
                        unique: fullAttribute.Unique || false,
                        mutability: 'readonly' as const,
                        returned: 'default' as const,
                        subAttributes: []
                    };

                    entityAttributesMap[entityName].push(attributeInfo);
                }
            });
        }
    });

    // Step 3: Create unified schema structure from entities
    const schemas: ProcessedSchema[] = entities.map((entity) => {
        const entityAttributes = entityAttributesMap[entity.DisplayName] || [];

        return {
            id: `urn:dcps:scim:v1:${entity.DisplayName}`,
            name: entity.DisplayName,
            type: 'Entity',
            description: entity.Description,
            attributes: entityAttributes,
            // Preserve the Entities property from the original schema entity, extracting just the Id values
            Entities: (entity as any).Entities ? (entity as any).Entities.map((ent: any) => ent.Id || ent) : []
        };
    });
    return schemas;
}

/**
 * Extract field options from schemas
 */
export function extractFieldOptions(schemas: ProcessedSchema[]): Array<{
    name: string;
    id: string;
    value: string;
    label: string;
    type: string;
    required: boolean;
    multiValued: boolean;
    isReference: boolean;
}> {
    const fieldOptions: Array<{
        name: string;
        id: string;
        value: string;
        label: string;
        type: string;
        required: boolean;
        multiValued: boolean;
        isReference: boolean;
    }> = [];
    const seenFields = new Set<string>();

    schemas.forEach(schema => {
        if (schema.attributes && Array.isArray(schema.attributes)) {
            schema.attributes.forEach((attr: ProcessedSchemaAttribute) => {
                // attr.name contains DisplayName (without $ prefix) for property matching
                // attr.id contains Id (with $ prefix) for attribute references
                const attrName = attr.name || '';
                const attrId = attr.id || attrName;
                // Use Id (with $ prefix) as the unique identifier
                if (attrId && !seenFields.has(attrId)) {
                    seenFields.add(attrId);
                    // Use attribute name (DisplayName) for labels
                    fieldOptions.push({
                        name: attrName, // Property name (without $ prefix) for entity property matching
                        id: attrId,     // Id (with $ prefix) for attribute references
                        value: attrId,  // Use Id (with $ prefix) as the value
                        label: attrName, // Display attribute name (DisplayName without $ prefix)
                        type: attr.type || 'string',
                        required: attr.required || false,
                        multiValued: attr.multiValued || false,
                        isReference: attr.isReference || false
                    });
                }
            });
        }
    });

    return fieldOptions.sort((a, b) => a.label.localeCompare(b.label));
}

